import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import currency from "currency.js";
import {
  AlertController,
  LoadingController,
  ModalController,
  NavParams,
} from "ionic-angular";
import moment from "moment";
import { Subscription } from "rxjs";
import {
  FacilityDetail,
  FacilityRate,
} from "../../../models/facilities/facility-details";
import { FacilitiesService } from "../../../providers/facilities-service/facilities.service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "rates-facility-care-level",
  templateUrl: "rates-facility-care-level.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatesFacilityCareLevel),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatesFacilityCareLevel),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RatesFacilityCareLevel implements OnInit, OnDestroy {
  innerFacilityRateForm: FormGroup;
  _saveEnabled: boolean = false;
  subscriptions: Subscription[] = [];

  @Input() currentFacility: FacilityDetail;
  @Input() careLevel: number;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onFacilityRateSaveSuccess = new EventEmitter<any>();

  facilityRates: FacilityRate[] = null;
  activeFacilityRate: FacilityRate = null;
  showRateForm: boolean = false;
  showAddRate: boolean = true;
  comment: string = "";
  error: string = "";
  constructor(
    protected formBuilder: FormBuilder,
    protected alertController: AlertController,
    protected navParams: NavParams,
    private facilityService: FacilitiesService,
    private cd: ChangeDetectorRef,
    private loadingCtrl: LoadingController,
    public modalCtrl: ModalController
  ) {}

  ngOnInit() {
    this.initInnerFacilityRateForm();

    this.cd.detectChanges();

    this.getFacilityRates(true);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  initInnerFacilityRateForm() {
    this.innerFacilityRateForm = this.formBuilder.group({
      initialized: [true],
      description: [""],
      rate: ["", [Validators.required]],
      effectiveDate: ["", [Validators.required]],
      expirationDate: ["-"],
      m1: [""],
      m2: [""],
      m3: [""],
      m4: [""],
    });

    this.subscriptions.push(
      this.innerFacilityRateForm.valueChanges.subscribe(this.onFormChanges)
    );
  }

  getFacilityRates(showLoad?: boolean) {
    const spinner = this.loadingCtrl.create({
      content: "Loading Facility Rates",
    });
    if (showLoad) spinner.present();
    this.subscriptions.push(
      this.facilityService
        .getFacilityRatesByCareLevel(
          this.currentFacility.facilityId,
          this.careLevel
        )
        .subscribe(
          (data) => {
            data.sort(
              (a, b) =>
                Date.parse(b.effectiveDate) - Date.parse(a.effectiveDate)
            );
            if (
              data.length > 0 &&
              (!data[0].expirationDate ||
                Date.parse(data[0].expirationDate) > Date.now())
            ) {
              this.activeFacilityRate = data.shift();
              this.showRateForm = true;
            } else {
              this.activeFacilityRate = null;
              this.showRateForm = false;
            }
            this.showAddRate = true;
            this.facilityRates = data;
            this.mapFacilityRateToForm();
            this.saveEnabled = false;
            if (showLoad) spinner.dismiss();
          },
          (error) => {
            if (showLoad) spinner.dismiss();
          }
        )
    );
  }

  mapFacilityRateToForm(clean?: boolean) {
    let description: string = "";
    let rate: string = "";
    let effectiveDate: string = "";
    let expirationDate: string = "-";
    let m1: string = "";
    let m2: string = "";
    let m3: string = "";
    let m4: string = "";

    if (this.activeFacilityRate && !clean) {
      description = this.activeFacilityRate.description;
      rate = this.currencyFormatter(
        this.activeFacilityRate.displayRate
      ).format();
      effectiveDate = this.activeFacilityRate.effectiveDate;
      expirationDate = this.activeFacilityRate.expirationDate
        ? moment(this.activeFacilityRate.expirationDate).format("MM/DD/YYYY")
        : "-";
      m1 = this.activeFacilityRate.m1;
      m2 = this.activeFacilityRate.m2;
      m3 = this.activeFacilityRate.m3;
      m4 = this.activeFacilityRate.m4;
    }

    this.innerFacilityRateForm.get("description").setValue(description);
    this.innerFacilityRateForm.get("rate").setValue(rate);
    this.innerFacilityRateForm.get("effectiveDate").setValue(effectiveDate);
    this.innerFacilityRateForm.get("expirationDate").setValue(expirationDate);
    this.innerFacilityRateForm.get("m1").setValue(m1);
    this.innerFacilityRateForm.get("m2").setValue(m2);
    this.innerFacilityRateForm.get("m3").setValue(m3);
    this.innerFacilityRateForm.get("m4").setValue(m4);
  }

  addNewRate() {
    if (this.activeFacilityRate) {
      this.facilityRates.unshift(this.activeFacilityRate);
    }
    this.mapFacilityRateToForm(true);
    this.showRateForm = true;
    this.showAddRate = false;
  }

  removeNewRate() {
    if (this.activeFacilityRate) {
      this.showRateForm = true;
      this.activeFacilityRate = this.facilityRates.shift();
      this.mapFacilityRateToForm();
    } else {
      this.showRateForm = false;
    }
    this.showAddRate = true;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  checkCanSave() {
    this.saveEnabled =
      this.innerFacilityRateForm &&
      this.innerFacilityRateForm.valid &&
      this.error.length == 0;
    //this.checkModifiersHasValue();
  }

  checkModifiersHasValue() {
    let m1 = this.innerFacilityRateForm.controls["m1"].value;
    let m2 = this.innerFacilityRateForm.controls["m2"].value;
    let m3 = this.innerFacilityRateForm.controls["m3"].value;
    let m4 = this.innerFacilityRateForm.controls["m4"].value;
    if (m1 && m2 && m3 && m4) {
      return true;
    }

    return false;
  }

  onFormChanges = (value) => {
    if (value) {
      this.checkCanSave();
    }
  };

  showSaveFacilityRateModal(deleteRate?: boolean) {
    if ((this.activeFacilityRate && this.showAlertPopUp()) || deleteRate) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Rate Change",
          commentPlaceholderKey: "saveFacilityRateCommentPlaceHolder",
          popupMessageKey: "saveFacilityRateWarningMessage",
          section: "facility",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-facility-confirmation-popup",
        }
      );

      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.comment = data.comment;
          this.saveFacilityRate(deleteRate);
        }
      });
      modalPage.present();
    } else {
      this.saveFacilityRate(false);
    }
  }

  showAlertPopUp() {
    const rate =
      this.currencyFormatter(this.activeFacilityRate.displayRate).value !=
      this.currencyFormatter(this.innerFacilityRateForm.get("rate").value)
        .value;
    const date =
      this.activeFacilityRate.effectiveDate !=
      moment(this.innerFacilityRateForm.get("effectiveDate").value).format(
        "YYYY-MM-DD"
      );

    return rate || date;
  }

  saveFacilityRate(deleteRate?: boolean) {
    let spinnerText = "Saving Facility Rate";
    if (deleteRate) {
      spinnerText = "Deleting Facility Rate";
    }
    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();

    const facilityRate: FacilityRate = {} as FacilityRate;
    facilityRate.facilityId = this.currentFacility.facilityId;
    facilityRate.level = this.careLevel;
    facilityRate.description =
      this.innerFacilityRateForm.get("description").value;
    facilityRate.displayRate = this.currencyFormatter(
      this.innerFacilityRateForm.get("rate").value
    ).value;
    facilityRate.effectiveDate = moment(
      this.innerFacilityRateForm.get("effectiveDate").value
    ).format("YYYY-MM-DD");
    facilityRate.isDeleted = deleteRate;
    facilityRate.comment = this.comment;
    facilityRate.m1 = this.innerFacilityRateForm.get("m1").value;
    facilityRate.m2 = this.innerFacilityRateForm.get("m2").value;
    facilityRate.m3 = this.innerFacilityRateForm.get("m3").value;
    facilityRate.m4 = this.innerFacilityRateForm.get("m4").value;

    if (
      this.showAddRate == true &&
      this.showRateForm == true &&
      this.activeFacilityRate
    ) {
      facilityRate.originalEffectiveDate =
        this.activeFacilityRate.effectiveDate;
    }

    this.facilityService
      .saveFacilityRate(facilityRate)
      .pipe()
      .take(1)
      .subscribe(
        () => {
          spinner.dismiss();
          this.showAddRate = true;
          this.activeFacilityRate = facilityRate;
          this.onFacilityRateSaveSuccess.emit(true);
          if (deleteRate) {
            this.removeNewRate();
            this.innerFacilityRateForm.get("expirationDate").setValue("-");
            this.onSaveSuccessAlert("Facility Rate Deleted");
          } else {
            this.onSaveSuccessAlert("Facility Rate Saved");
          }
          this.saveEnabled = false;
          setTimeout(() => {
            this.getFacilityRates(false);
          }, 5000);
        },
        (error) => {
          spinner.dismiss();
          this.onSaveError(error);
        }
      );
  }

  private onSaveSuccessAlert = (message: string) => {
    this.showAlert({
      title: "Success",
      message: message,
    });
  };

  private onSaveError = (error) => {
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    }
  };

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  cancel() {
    this.onCanceled.emit(true);
  }

  checkChar(event: any, type, control) {
    let newValue = event.target.value;
    if (type == "alpha") {
      let regExp = new RegExp("^[A-Za-z?]+$");
      if (!regExp.test(newValue)) {
        this.innerFacilityRateForm.controls[control].setValue(
          newValue.slice(0, -1)
        );
      }
    } else if (type == "alphaNumeric") {
      let regExp = new RegExp("^[A-Za-z0-9?]+$");
      if (!regExp.test(newValue)) {
        this.innerFacilityRateForm.controls[control].setValue(
          newValue.slice(0, -1)
        );
      }
    } else if (type == "numeric") {
      let regExp = new RegExp("^[0-9]+$");
      if (!regExp.test(newValue)) {
        this.innerFacilityRateForm.controls[control].setValue(
          newValue.slice(0, -1)
        );
      }
    }
    this.codeChanged();
    return;
  }

  currencyFormatter(value: currency.Any): currency {
    return currency(value, {
      separator: ",",
      decimal: ".",
      precision: 2,
    });
  }

  codeChanged() {
    this.error = this.checkValidModifiers();
    this.checkCanSave();
  }

  checkValidModifiers() {
    let m1 = this.innerFacilityRateForm.controls["m1"].value;
    let m2 = this.innerFacilityRateForm.controls["m2"].value;
    let m3 = this.innerFacilityRateForm.controls["m3"].value;
    let m4 = this.innerFacilityRateForm.controls["m4"].value;
    if (
      (m1 && m1.length == 1) ||
      (m2 && m2.length == 1) ||
      (m3 && m3.length == 1) ||
      (m4 && m4.length == 1)
    )
      return "lengthError";

    if (m4 && m4.length > 0) {
      return m3 && m3.length > 0 && m2 && m2.length > 0 && m1 && m1.length > 0
        ? ""
        : "orderError";
    } else if (m3 && m3.length > 0) {
      return m2 && m2.length > 0 && m1 && m1.length > 0 ? "" : "orderError";
    } else if (m2 && m2.length > 0) {
      return m1 && m1.length > 0 ? "" : "orderError";
    }

    return "";
  }
}



// WEBPACK FOOTER //
// ./src/components/facilities/rates-facility-care-level/rates-facility-care-level.ts